import { useEffect, useRef, useState, useCallback } from 'react';

interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  delay?: number;
  enabled?: boolean;
}

export function useAutoSave<T>({
  data,
  onSave,
  delay = 1500,
  enabled = true,
}: UseAutoSaveOptions<T>) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousDataRef = useRef<string>(JSON.stringify(data));
  const isFirstRender = useRef(true);
  const onSaveRef = useRef(onSave);

  // Keep onSave ref updated
  onSaveRef.current = onSave;

  const save = useCallback(async (dataToSave: T) => {
    setIsSaving(true);
    setError(null);
    try {
      await onSaveRef.current(dataToSave);
      setLastSaved(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    // Skip first render (don't save on mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousDataRef.current = JSON.stringify(data);
      return;
    }

    if (!enabled) return;

    const currentDataStr = JSON.stringify(data);

    // Only save if data changed
    if (currentDataStr === previousDataRef.current) {
      return;
    }
    previousDataRef.current = currentDataStr;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new debounced save
    timeoutRef.current = setTimeout(() => {
      save(data);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, enabled, save]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Expose a function to save immediately (flush pending save)
  // Note: This works even when auto-save is disabled, for manual saves
  const saveNow = useCallback(async () => {
    if (data === null || data === undefined) return;

    // Clear any pending debounced save
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    await save(data);
  }, [data, save]);

  return { isSaving, lastSaved, error, saveNow };
}
